import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { API } from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, fmtDate, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, Checks, CreditCard, Copy, EnvelopeSimple, DotsThreeVertical, PencilSimple, Trash, Eye, DownloadSimple, PaperPlaneRight } from "@phosphor-icons/react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from "@/components/ui/dropdown-menu";

const empty = { client_id: "", project_ids: [], amount: 0, line_items: [], status: "draft", due_at: "", notes: "" };

// Defaults & helpers for itemized fees (mileage, travel, overage, cancellation, custom).
const LINE_ITEM_PRESETS = {
  mileage:      { description: "Mileage",           unit_price: 0.67, quantity: 1, unit_label: "miles",  suffix: "/mi" },
  travel_time:  { description: "Travel time",       unit_price: 45.00, quantity: 1, unit_label: "hours",  suffix: "/hr" },
  overage:      { description: "Overage hours",     unit_price: 75.00, quantity: 1, unit_label: "hours",  suffix: "/hr" },
  cancellation: { description: "Cancellation fee",  unit_price: 150.00, quantity: 1, unit_label: "flat",  suffix: "" },
  other:        { description: "",                  unit_price: 0.00, quantity: 1, unit_label: "qty",     suffix: "" },
};
const KIND_LABELS = { service: "Service", mileage: "Mileage", travel_time: "Travel time", overage: "Overage", cancellation: "Cancellation fee", other: "Other" };
const roundMoney = (n) => Math.round((Number(n) || 0) * 100) / 100;

export default function Invoices() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null); // invoice being viewed in detail sheet

  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });

  const save = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.patch(`/invoices/${editing.invoice_id}`, payload)).data;
      return (await api.post("/invoices", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Invoice updated" : "Invoice created");
      setOpen(false); setForm(empty); setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const markPaid = useMutation({
    mutationFn: async ({ id, payment_method, reference, note }) => (
      await api.post(`/invoices/${id}/mark-paid`, { payment_method, reference, note })
    ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Marked as paid (${(vars.payment_method || "manual")})`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const markPaidWithMethod = (id, invoiceNumber, method) => {
    const label = { zelle: "Zelle", cash: "Cash", check: "Check", wire: "Wire transfer", other: "Other" }[method] || method;
    let reference = null;
    if (method === "zelle") {
      reference = window.prompt(`Zelle confirmation # for ${invoiceNumber} (optional):`, "");
      if (reference === null) return; // user cancelled
    } else if (method === "check") {
      reference = window.prompt(`Check # for ${invoiceNumber} (optional):`, "");
      if (reference === null) return;
    }
    if (!window.confirm(`Confirm ${invoiceNumber} was paid by ${label}?${reference ? ` Ref: ${reference}.` : ""} This will also confirm any linked pending projects.`)) return;
    markPaid.mutate({ id, payment_method: method, reference: reference || undefined });
  };

  const createStripeLink = useMutation({
    mutationFn: async (invoice_id) => (await api.post("/payments/invoice-checkout", { invoice_id, origin_url: window.location.origin })).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      navigator.clipboard?.writeText(data.checkout_url).catch(() => {});
      toast.success("Stripe link copied to clipboard", { description: "Paste it in an email or open it directly.", action: { label: "Open", onClick: () => window.open(data.checkout_url, "_blank") } });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Stripe link failed"),
  });

  const emailInvoice = useMutation({
    mutationFn: async ({ invoice_id, wasSent }) => {
      const r = await api.post(`/emails/invoice/${invoice_id}`, { include_stripe_link: true, origin_url: window.location.origin });
      return { ...r.data, wasSent };
    },
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success(d.wasSent ? "Payment link resent — fresh Stripe URL delivered" : "Invoice emailed with Stripe link");
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail || e?.message || "Email failed";
      // Show the actual error for long text (Resend domain-not-verified etc.) — critical for debugging deliverability.
      toast.error(detail, { duration: 12000, description: detail.length > 60 ? "Check Resend domain verification — see the Communications tab for details." : undefined });
    },
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/invoices/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); toast.success("Invoice deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const { data: billConfig } = useQuery({
    queryKey: ["bill-config"],
    queryFn: async () => {
      try { return (await api.get("/bill/config")).data; }
      catch { return { configured: false }; }
    },
    retry: false,
    staleTime: 60_000,
  });

  const syncBill = useMutation({
    mutationFn: async (invoice_id) => (await api.post("/bill/sync-invoice", { invoice_id })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success("Invoice synced to Bill.com");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bill.com sync failed"),
  });

  const clientProjects = useMemo(() => projects.filter(p => p.client_id === form.client_id), [projects, form.client_id]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (i) => {
    setEditing(i);
    setForm({ ...empty, ...i, line_items: i.line_items || [], due_at: i.due_at?.slice(0,10) || "" });
    setOpen(true);
  };

  // Line-items editor helpers
  const recomputeTotal = (items, base = 0) => {
    const sum = (items || []).reduce((s, li) => s + roundMoney((li.quantity || 0) * (li.unit_price || 0)), 0);
    return roundMoney(base + sum);
  };
  const addLineItem = (kind) => {
    const preset = LINE_ITEM_PRESETS[kind] || LINE_ITEM_PRESETS.other;
    const li = { kind, description: preset.description, quantity: preset.quantity, unit_price: preset.unit_price, amount: roundMoney(preset.quantity * preset.unit_price), project_id: null };
    const next = [...(form.line_items || []), li];
    setForm({ ...form, line_items: next, amount: recomputeTotal(next, projectsSubtotal()) });
  };

  const autoMileage = useMutation({
    mutationFn: async (project_id) => (await api.post("/geo/mileage", { project_id, round_trip: true })).data,
    onSuccess: (data, project_id) => {
      const p = projects.find((x) => x.project_id === project_id);
      const li = {
        kind: "mileage",
        description: `Round trip · office → ${p?.location || "destination"}`,
        quantity: data.miles,
        unit_price: data.rate,
        amount: data.amount,
        project_id,
      };
      const next = [...(form.line_items || []), li];
      setForm({ ...form, line_items: next, amount: recomputeTotal(next, projectsSubtotal()) });
      toast.success(`Added ${data.miles} mi × $${data.rate}/mi = $${data.amount.toFixed(2)}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Mileage lookup failed"),
  });
  const updateLineItem = (idx, patch) => {
    const next = form.line_items.map((li, i) => {
      if (i !== idx) return li;
      const merged = { ...li, ...patch };
      merged.amount = roundMoney((merged.quantity || 0) * (merged.unit_price || 0));
      return merged;
    });
    setForm({ ...form, line_items: next, amount: recomputeTotal(next, projectsSubtotal()) });
  };
  const removeLineItem = (idx) => {
    const next = form.line_items.filter((_, i) => i !== idx);
    setForm({ ...form, line_items: next, amount: recomputeTotal(next, projectsSubtotal()) });
  };
  const projectsSubtotal = () => {
    return (form.project_ids || []).reduce((s, pid) => {
      const p = projects.find((x) => x.project_id === pid);
      if (!p) return s;
      return s + roundMoney((p.client_rate || 0) * (p.duration_hours || 1));
    }, 0);
  };

  const clientName = (id) => clients.find(c => c.client_id === id)?.name || "—";

  const totalOutstanding = invoices.filter(i => i.status !== "paid").reduce((s, i) => s + (i.amount || 0), 0);
  const totalPaid = invoices.filter(i => i.status === "paid").reduce((s, i) => s + (i.amount || 0), 0);

  return (
    <div data-testid="invoices-page">
      <PageHeader
        eyebrow="Billing"
        title="Invoices"
        description="Generate invoices per assignment and track outstanding balances."
        actions={
          <Button onClick={openNew} data-testid="add-invoice-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
            <Plus size={16} weight="bold" className="mr-1.5" /> New invoice
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MiniCard label="Outstanding" value={fmtMoney(totalOutstanding)} tone="amber" />
        <MiniCard label="Paid (all-time)" value={fmtMoney(totalPaid)} tone="emerald" />
        <MiniCard label="Invoices" value={invoices.length} tone="blue" />
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : invoices.length === 0 ? (
        <Empty title="No invoices yet" hint="Create your first invoice to start billing." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Invoice #</th>
                <th className="text-left px-3 py-3 font-semibold">Client</th>
                <th className="text-left px-3 py-3 font-semibold">Issued</th>
                <th className="text-left px-3 py-3 font-semibold">Due</th>
                <th className="text-right px-3 py-3 font-semibold">Amount</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((i) => (
                <tr key={i.invoice_id} className="data-row border-t border-slate-100" data-testid={`invoice-row-${i.invoice_id}`}>
                  <td className="px-5 py-3 font-mono">
                    <button
                      type="button"
                      onClick={() => setViewing(i)}
                      className="text-blue-800 hover:underline font-semibold"
                      data-testid={`invoice-view-${i.invoice_id}`}
                    >
                      {i.invoice_number}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{clientName(i.client_id)}</td>
                  <td className="px-3 py-3 text-slate-600 text-xs">{fmtDate(i.issued_at)}</td>
                  <td className="px-3 py-3 text-slate-600 text-xs">{i.due_at ? fmtDate(i.due_at) : "—"}</td>
                  <td className="px-3 py-3 text-right font-mono">{fmtMoney(i.amount)}</td>
                  <td className="px-3 py-3"><StatusBadge status={i.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`invoice-actions-${i.invoice_id}`}>
                          <DotsThreeVertical size={16} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-white">
                        <DropdownMenuLabel className="text-xs text-slate-500">Invoice actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => setViewing(i)} data-testid={`view-invoice-${i.invoice_id}`}>
                          <Eye size={14} className="mr-2" /> View details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => window.open(`${API}/invoices/${i.invoice_id}/pdf`, "_blank")} data-testid={`download-invoice-pdf-${i.invoice_id}`}>
                          <DownloadSimple size={14} className="mr-2" /> Download PDF
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openEdit(i)} data-testid={`edit-invoice-${i.invoice_id}`}>
                          <PencilSimple size={14} className="mr-2" /> Edit invoice
                        </DropdownMenuItem>
                        {i.status !== "paid" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => emailInvoice.mutate({ invoice_id: i.invoice_id, wasSent: !!i.last_emailed_at })} data-testid={`email-invoice-${i.invoice_id}`}>
                              <EnvelopeSimple size={14} className="mr-2" /> {i.last_emailed_at ? "Resend payment link" : "Email + pay link"}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => createStripeLink.mutate(i.invoice_id)} data-testid={`stripe-link-${i.invoice_id}`}>
                              <CreditCard size={14} className="mr-2" /> Copy Stripe link
                            </DropdownMenuItem>
                            <DropdownMenuSub>
                              <DropdownMenuSubTrigger data-testid={`mark-paid-${i.invoice_id}`} className="text-emerald-700">
                                <Checks size={14} className="mr-2" /> Mark as paid…
                              </DropdownMenuSubTrigger>
                              <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                  <DropdownMenuLabel className="text-[11px]">Payment method</DropdownMenuLabel>
                                  <DropdownMenuItem data-testid={`mark-paid-zelle-${i.invoice_id}`} onClick={() => markPaidWithMethod(i.invoice_id, i.invoice_number, "zelle")}>Zelle</DropdownMenuItem>
                                  <DropdownMenuItem data-testid={`mark-paid-cash-${i.invoice_id}`} onClick={() => markPaidWithMethod(i.invoice_id, i.invoice_number, "cash")}>Cash</DropdownMenuItem>
                                  <DropdownMenuItem data-testid={`mark-paid-check-${i.invoice_id}`} onClick={() => markPaidWithMethod(i.invoice_id, i.invoice_number, "check")}>Check</DropdownMenuItem>
                                  <DropdownMenuItem data-testid={`mark-paid-wire-${i.invoice_id}`} onClick={() => markPaidWithMethod(i.invoice_id, i.invoice_number, "wire")}>Wire transfer</DropdownMenuItem>
                                  <DropdownMenuItem data-testid={`mark-paid-other-${i.invoice_id}`} onClick={() => markPaidWithMethod(i.invoice_id, i.invoice_number, "other")}>Other</DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuPortal>
                            </DropdownMenuSub>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => billConfig?.configured ? syncBill.mutate(i.invoice_id) : toast.error("Bill.com not configured — add BILL_* env vars.")}
                              disabled={!billConfig?.configured || syncBill.isPending}
                              data-testid={`bill-sync-${i.invoice_id}`}
                            >
                              <Copy size={14} className="mr-2" />
                              {i.bill_invoice_id ? `Re-sync to Bill.com` : `Sync to Bill.com${!billConfig?.configured ? " (not configured)" : ""}`}
                            </DropdownMenuItem>
                            {i.bill_invoice_id && (
                              <div className="px-2 py-1 text-[10px] text-slate-500 font-mono">
                                BILL: {i.bill_invoice_id}{i.bill_status ? ` · ${i.bill_status}` : ""}
                              </div>
                            )}
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          data-testid={`delete-invoice-${i.invoice_id}`}
                          onSelect={(e) => {
                            e.preventDefault();
                            setTimeout(() => {
                              if (window.confirm(`Delete invoice ${i.invoice_number}? This cannot be undone.`)) {
                                del.mutate(i.invoice_id);
                              }
                            }, 50);
                          }}
                        >
                          <Trash size={14} className="mr-2" /> Delete invoice
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice detail sheet — click any invoice number to open */}
      <Sheet open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <SheetContent className="w-full sm:max-w-2xl bg-white overflow-y-auto" data-testid="invoice-detail-sheet">
          {viewing && (() => {
            const linkedProjects = projects.filter((p) => (viewing.project_ids || []).includes(p.project_id));
            const client = clients.find((c) => c.client_id === viewing.client_id);
            const pdfUrl = `${API}/invoices/${viewing.invoice_id}/pdf`;
            return (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center justify-between">
                    <span className="font-mono">{viewing.invoice_number}</span>
                    <StatusBadge status={viewing.status} />
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3 text-xs border border-slate-200 rounded-md p-3 bg-slate-50">
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Client</span>{client?.name || "—"}</div>
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Contact</span>{client?.contact_name || "—"}</div>
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Email</span>{client?.email || "—"}</div>
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Amount</span><span className="font-mono font-bold text-slate-900">{fmtMoney(viewing.amount)}</span></div>
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Issued</span>{fmtDate(viewing.issued_at)}</div>
                    <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Due</span>{viewing.due_at ? fmtDate(viewing.due_at) : "—"}</div>
                    {viewing.paid_at && <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Paid</span>{fmtDate(viewing.paid_at)}</div>}
                    {viewing.payment_method && <div><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Method</span>{viewing.payment_method}{viewing.payment_reference ? ` · ${viewing.payment_reference}` : ""}</div>}
                    {viewing.last_emailed_at && <div className="col-span-2"><span className="uppercase tracking-wider text-slate-400 text-[10px] block">Last sent</span>{fmtDate(viewing.last_emailed_at)} to {viewing.last_email_to || client?.email || "—"}</div>}
                  </div>

                  {linkedProjects.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Linked assignments</div>
                      <div className="border border-slate-200 rounded-md divide-y">
                        {linkedProjects.map((p) => (
                          <div key={p.project_id} className="p-3 flex items-center justify-between text-sm" data-testid={`invoice-project-${p.project_id}`}>
                            <div>
                              <div className="font-semibold text-slate-900">{p.title}</div>
                              <div className="text-[11px] text-slate-500">{p.project_type} · {p.language} · {fmtDate(p.scheduled_at)}</div>
                            </div>
                            <div className="font-mono text-sm text-slate-700">{fmtMoney((p.client_rate || 0) * (p.duration_hours || 1))}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(viewing.line_items || []).length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Additional line items</div>
                      <div className="border border-slate-200 rounded-md divide-y">
                        {viewing.line_items.map((li, idx) => (
                          <div key={idx} className="p-3 flex items-center justify-between text-sm" data-testid={`invoice-lineitem-${idx}`}>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                                  {KIND_LABELS[li.kind] || li.kind}
                                </span>
                                <span className="font-semibold text-slate-900">{li.description || KIND_LABELS[li.kind]}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                                {li.quantity}{(LINE_ITEM_PRESETS[li.kind]?.unit_label || "").startsWith("miles") ? " mi" : (LINE_ITEM_PRESETS[li.kind]?.unit_label === "hours" ? " hr" : "")} × ${Number(li.unit_price).toFixed(2)}
                              </div>
                            </div>
                            <div className="font-mono text-sm text-slate-700">{fmtMoney(li.amount || (li.quantity * li.unit_price))}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {viewing.notes && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Notes</div>
                      <div className="border border-slate-200 rounded-md p-3 bg-white text-sm text-slate-700 whitespace-pre-wrap">{viewing.notes}</div>
                    </div>
                  )}

                  {viewing.last_email_html ? (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Email preview</div>
                      <iframe
                        title="Invoice email preview"
                        srcDoc={viewing.last_email_html}
                        className="w-full h-[400px] border border-slate-200 rounded-md bg-white"
                        sandbox=""
                        data-testid="invoice-email-iframe"
                      />
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400 italic">
                      No email preview available — this invoice hasn't been emailed yet, or was sent before email tracking was enabled. Click "Resend to client" to email it now.
                    </div>
                  )}

                  <div className="flex items-center flex-wrap gap-2 pt-3 border-t border-slate-100">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => window.open(pdfUrl, "_blank")}
                      data-testid="invoice-detail-download-pdf"
                    >
                      <DownloadSimple size={14} className="mr-1.5" /> Download PDF
                    </Button>
                    {viewing.status !== "paid" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => emailInvoice.mutate({ invoice_id: viewing.invoice_id, wasSent: !!viewing.last_emailed_at })}
                          disabled={emailInvoice.isPending}
                          className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                          data-testid="invoice-detail-resend-btn"
                        >
                          <PaperPlaneRight size={14} className="mr-1.5" />
                          {viewing.last_emailed_at ? "Resend to client" : "Email to client"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => createStripeLink.mutate(viewing.invoice_id)}
                          data-testid="invoice-detail-stripe-btn"
                        >
                          <CreditCard size={14} className="mr-1.5" /> Copy Stripe link
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle>{editing ? "Edit invoice" : "New invoice"}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4" data-testid="invoice-form">
            <Field label="Client">
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, project_ids: [] })}>
                <SelectTrigger data-testid="inv-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>

            {form.client_id && (
              <Field label="Link projects">
                <div className="border border-slate-200 rounded-md divide-y max-h-40 overflow-auto">
                  {clientProjects.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400">No projects for this client.</div>
                  ) : clientProjects.map(p => {
                    const checked = form.project_ids.includes(p.project_id);
                    return (
                      <label key={p.project_id} className="flex items-center gap-2 p-2 text-sm hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked ? form.project_ids.filter(x => x !== p.project_id) : [...form.project_ids, p.project_id];
                            const projSum = clientProjects.filter(cp => next.includes(cp.project_id)).reduce((s, cp) => s + (cp.client_rate || 0) * (cp.duration_hours || 1), 0);
                            const liSum = (form.line_items || []).reduce((s, li) => s + roundMoney((li.quantity || 0) * (li.unit_price || 0)), 0);
                            setForm({ ...form, project_ids: next, amount: roundMoney(projSum + liSum) });
                          }}
                        />
                        <span className="flex-1">{p.title}</span>
                        <span className="font-mono text-xs text-slate-500">{fmtMoney((p.client_rate || 0) * (p.duration_hours || 1))}</span>
                      </label>
                    );
                  })}
                </div>
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount ($)"><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} data-testid="inv-amount" /></Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["draft","sent","paid","overdue"].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </div>

            {/* Itemized line items — mileage, travel time, overage, cancellation, custom */}
            <div className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="line-items-editor">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-xs font-semibold text-slate-900">Additional line items</div>
                  <div className="text-[11px] text-slate-500">Mileage, travel time, overage, cancellation fees — added on top of linked assignment charges.</div>
                </div>
                <Select value="" onValueChange={(v) => v && addLineItem(v)}>
                  <SelectTrigger className="w-40 h-8 text-xs bg-white" data-testid="add-line-item-select"><SelectValue placeholder="+ Add line…" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="mileage">Mileage (IRS $0.67/mi)</SelectItem>
                    <SelectItem value="travel_time">Travel time</SelectItem>
                    <SelectItem value="overage">Overage hours</SelectItem>
                    <SelectItem value="cancellation">Cancellation fee</SelectItem>
                    <SelectItem value="other">Custom line</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Auto-mileage suggestions per linked project with a location */}
              {(form.project_ids || []).length > 0 && (() => {
                const withLoc = (form.project_ids || [])
                  .map((pid) => projects.find((p) => p.project_id === pid))
                  .filter((p) => p && p.location);
                if (!withLoc.length) return null;
                return (
                  <div className="mb-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className="text-slate-500">Auto-mileage from office:</span>
                    {withLoc.map((p) => (
                      <button
                        key={p.project_id}
                        type="button"
                        onClick={() => autoMileage.mutate(p.project_id)}
                        disabled={autoMileage.isPending}
                        className="px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 hover:bg-blue-100 disabled:opacity-50 truncate max-w-[240px]"
                        data-testid={`auto-mileage-${p.project_id}`}
                        title={p.location}
                      >
                        📍 {p.location}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {(form.line_items || []).length === 0 ? (
                <div className="text-[11px] text-slate-400 italic py-2">No additional line items. Use the picker above to add mileage, travel time, overage, or a cancellation fee.</div>
              ) : (
                <div className="space-y-2">
                  {form.line_items.map((li, idx) => {
                    const preset = LINE_ITEM_PRESETS[li.kind] || LINE_ITEM_PRESETS.other;
                    return (
                      <div key={idx} className="bg-white border border-slate-200 rounded-md p-2 space-y-2" data-testid={`line-item-${idx}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-widest font-bold text-blue-800 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 shrink-0">
                            {KIND_LABELS[li.kind]}
                          </span>
                          <Input
                            value={li.description}
                            placeholder={KIND_LABELS[li.kind]}
                            onChange={(e) => updateLineItem(idx, { description: e.target.value })}
                            className="h-8 text-sm flex-1"
                            data-testid={`line-item-desc-${idx}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeLineItem(idx)}
                            className="text-slate-400 hover:text-red-600 p-1"
                            title="Remove"
                            data-testid={`line-item-remove-${idx}`}
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Qty ({preset.unit_label})</div>
                            <Input
                              type="number" step="0.01" min="0"
                              value={li.quantity}
                              onChange={(e) => updateLineItem(idx, { quantity: parseFloat(e.target.value || 0) })}
                              className="h-8 text-sm"
                              data-testid={`line-item-qty-${idx}`}
                            />
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Unit price {preset.suffix}</div>
                            <Input
                              type="number" step="0.01" min="0"
                              value={li.unit_price}
                              onChange={(e) => updateLineItem(idx, { unit_price: parseFloat(e.target.value || 0) })}
                              className="h-8 text-sm font-mono"
                              data-testid={`line-item-unit-${idx}`}
                            />
                          </div>
                          <div>
                            <div className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Amount</div>
                            <div className="h-8 flex items-center justify-end px-2 font-mono text-sm font-bold text-slate-900" data-testid={`line-item-amount-${idx}`}>
                              ${roundMoney((li.quantity || 0) * (li.unit_price || 0)).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-200 text-xs">
                    <span className="text-slate-500">Assignments subtotal: <span className="font-mono">{fmtMoney(projectsSubtotal())}</span> · Line items: <span className="font-mono">{fmtMoney(form.line_items.reduce((s,li)=>s+roundMoney((li.quantity||0)*(li.unit_price||0)),0))}</span></span>
                    <span className="font-semibold text-slate-900">Total: <span className="font-mono">{fmtMoney(form.amount)}</span></span>
                  </div>
                </div>
              )}
            </div>
            <Field label="Due date"><Input type="date" value={form.due_at?.slice(0,10) || ""} onChange={(e) => setForm({ ...form, due_at: e.target.value })} /></Field>
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={() => save.mutate(form)} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="inv-save">
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function MiniCard({ label, value, tone }) {
  const tones = {
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-800",
    blue: "bg-blue-50 border-blue-100 text-blue-800",
  };
  return (
    <div className={`card-flat p-5 ${tones[tone]}`}>
      <div className="text-[11px] uppercase tracking-[0.15em] font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}
